import React, { useEffect, useState } from 'react';
import { GDPR_ALLOW_ANALYTICS_COOKIE } from '@techchange/base';
import { useCookies } from 'react-cookie';
import ReactGA from 'react-ga4';

// Keep track of the list of pages that are not allowed to run Google Analytics
const GA_BLOCKED_PAGES = [
	'/oauth/sso',
];

/**
 * This hook initializes tracking via Google Analytics.
 *
 * @param {string} pathname        The URL pathname of the current page.
 * @param {string} myOrganization  The organization.
 */
const useGoogleAnalytics = (pathname, myOrganization) => {

	const [cookies] = useCookies([GDPR_ALLOW_ANALYTICS_COOKIE]);

	const [trackers, setTrackers] = useState(null);

	// Check to make sure that the current user has opted-in to allowing analytics cookies...
	const hasAllowedAnalyticsCookies = cookies[GDPR_ALLOW_ANALYTICS_COOKIE] === 'true';

	// Initialize Google Analytics on the initial page
	useEffect(() => {

		// Check to make sure that the current user has opted-in to allowing analytics cookies...
		if (hasAllowedAnalyticsCookies && trackers === null) {

			// Check if the organization has provided a custom Google Analytics account...
			if (myOrganization?.data?.googleAnalytics?.length > 0) {

				// Check to make sure that Google Analytics is allowed to run on the current page...
				if (!GA_BLOCKED_PAGES.includes(pathname)) {

					const gaTrackers = [];

					for (const gaTracker of myOrganization.data.googleAnalytics) {

						// Ensure that tracker is enabled and has tracking ID and name
						if (gaTracker.ENABLED && gaTracker.TRACKING_ID && gaTracker.NAME) {

							// Add to trackers
							gaTrackers.push({
								trackingId: gaTracker.TRACKING_ID,
								gaOptions: {
									name: gaTracker.NAME
								},
								gtagOptions: {
									// Disables default tracking of page views
									// introduced in GA4
									send_page_view: false,
								},
							});

						}

					}

					// Initialize the trackers
					ReactGA.initialize(gaTrackers);

					// Note the list of tracks
					setTrackers(gaTrackers);

				}

			}

		}

	}, [myOrganization, pathname, hasAllowedAnalyticsCookies, trackers]);

	// Listen for page changes and send updates to Google Analytics
	useEffect(() => {

		// Check to make sure that the current user has opted-in to allowing analytics cookies...
		if (hasAllowedAnalyticsCookies && trackers && trackers.length > 0) {

			// Check to make sure that Google Analytics is allowed to run on the current page...
			if (!GA_BLOCKED_PAGES.includes(pathname)) {

				// Log the view of the current page...
				ReactGA.set({ page: window.location.pathname });
				ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

			}

		}

	}, [pathname, hasAllowedAnalyticsCookies, trackers]);

};

export default useGoogleAnalytics;
